import type { MapEntityPath } from '@common/schemas/entities/location.schema';
import type { PlacedSymbol } from './types';
import type { Vertex } from './path-geometry';
import { getOrganicPathPoints, getRiverJunctionWidth } from './path-geometry';

type ToScreen = (x: number, y: number) => { sx: number; sy: number };

interface RenderPathsOptions {
    selectedUid: string | null;
    hoveredUid: string | null;
    zoom: number;
    placedSymbols: PlacedSymbol[];
}

/**
 * Draws all placed paths onto a 2D canvas context.
 */
export function renderPaths(
    ctx: CanvasRenderingContext2D,
    paths: MapEntityPath[],
    toScreen: ToScreen,
    opts: RenderPathsOptions,
): void {
    const { selectedUid, hoveredUid, zoom, placedSymbols } = opts;

    for (const path of paths) {
        if (path.vertices.length < 2) continue;
        const { style, uid, pathType } = path;
        const isSelected = selectedUid === uid;
        const isHovered = hoveredUid === uid && !isSelected;

        // Compute natural meandered geometry using all paths for junction snapping
        const meanderedPoints = getOrganicPathPoints(path.vertices, pathType, placedSymbols, paths, path.uid);
        const screenVertices = meanderedPoints.map(v => toScreen(v.x, v.y));

        if (pathType === 'river') {
            // Tapered segment-by-segment drawing for rivers
            const endVertex = path.vertices[path.vertices.length - 1];
            const junctionWidth = getRiverJunctionWidth(endVertex, paths, path.uid, style.strokeWidth);

            const startWidth = style.strokeWidth * 0.25 * zoom * (isSelected ? 1.4 : isHovered ? 1.2 : 1);
            const endWidth = junctionWidth * zoom * (isSelected ? 1.4 : isHovered ? 1.2 : 1);

            for (let i = 0; i < screenVertices.length - 1; i++) {
                const p0 = screenVertices[i];
                const p1 = screenVertices[i + 1];
                const t = i / (screenVertices.length - 1);
                const currentWidth = startWidth + (endWidth - startWidth) * t;

                ctx.save();
                ctx.globalAlpha = style.opacity * (isHovered ? 0.85 : 1);
                ctx.strokeStyle = isSelected ? lighten(style.strokeColor, 40) : style.strokeColor;
                ctx.lineWidth = currentWidth;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';

                if (isSelected) {
                    ctx.shadowColor = 'rgba(255,255,255,0.6)';
                    ctx.shadowBlur = 8;
                }

                ctx.beginPath();
                ctx.moveTo(p0.sx, p0.sy);
                ctx.lineTo(p1.sx, p1.sy);
                ctx.stroke();
                ctx.restore();
            }
        } else {
            // Standard single-stroke polyline drawing for roads and routes
            ctx.save();
            ctx.globalAlpha = style.opacity * (isHovered ? 0.85 : 1);
            ctx.strokeStyle = isSelected ? lighten(style.strokeColor, 40) : style.strokeColor;
            ctx.lineWidth = style.strokeWidth * zoom * (isSelected ? 1.4 : isHovered ? 1.2 : 1);
            ctx.lineCap = style.lineCap;
            ctx.lineJoin = style.lineJoin;

            if (style.dashed) {
                const dashLen = Math.max(8, style.strokeWidth * 3 * zoom);
                ctx.setLineDash([dashLen, dashLen * 0.6]);
            } else {
                ctx.setLineDash([]);
            }

            if (isSelected) {
                ctx.shadowColor = 'rgba(255,255,255,0.6)';
                ctx.shadowBlur = 8;
            }

            drawPolyline(ctx, screenVertices);
            ctx.stroke();
            ctx.restore();
        }

        // Label
        if (path.name) {
            renderPathLabel(ctx, path.name, screenVertices, zoom);
        }

        // Vertex handles for editing
        if (isSelected) {
            const handleScreenVerts = path.vertices.map(v => toScreen(v.x, v.y));
            renderVertexHandles(ctx, handleScreenVerts, style.strokeColor);
        }
    }
}

/**
 * Draws an in-progress path preview.
 */
export function renderInProgressPath(
    ctx: CanvasRenderingContext2D,
    vertices: Vertex[],
    cursorVertex: Vertex | null,
    toScreen: ToScreen,
    zoom: number,
): void {
    if (vertices.length === 0) return;

    const screenVertices = vertices.map(v => toScreen(v.x, v.y));
    const allVerts: { sx: number; sy: number }[] = [...screenVertices];

    if (cursorVertex) {
        const { sx, sy } = toScreen(cursorVertex.x, cursorVertex.y);
        allVerts.push({ sx, sy });
    }

    ctx.save();
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([8, 4]);
    ctx.globalAlpha = 0.9;

    drawPolyline(ctx, allVerts);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#f59e0b';
    for (const { sx, sy } of screenVertices) {
        ctx.beginPath();
        ctx.arc(sx, sy, Math.max(4, 3 * zoom), 0, Math.PI * 2);
        ctx.fill();
    }

    if (screenVertices.length >= 1 && cursorVertex) {
        const first = screenVertices[0];
        ctx.beginPath();
        ctx.arc(first.sx, first.sy, 8, 0, Math.PI * 2);
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }

    ctx.restore();
}

function drawPolyline(ctx: CanvasRenderingContext2D, verts: { sx: number; sy: number }[]): void {
    if (verts.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(verts[0].sx, verts[0].sy);
    for (let i = 1; i < verts.length; i++) {
        ctx.lineTo(verts[i].sx, verts[i].sy);
    }
}

function renderVertexHandles(
    ctx: CanvasRenderingContext2D,
    verts: { sx: number; sy: number }[],
    accentColor: string,
): void {
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    for (let i = 0; i < verts.length; i++) {
        const { sx, sy } = verts[i];
        ctx.beginPath();
        ctx.arc(sx, sy, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = accentColor;
        ctx.beginPath();
        ctx.arc(sx, sy, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
    }
    ctx.restore();
}

function renderPathLabel(
    ctx: CanvasRenderingContext2D,
    name: string,
    verts: { sx: number; sy: number }[],
    zoom: number,
): void {
    const mid = Math.floor(verts.length / 2);
    const { sx, sy } = verts[mid];
    ctx.save();
    ctx.font = `bold ${Math.max(9, 11 * zoom)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.globalAlpha = 0.85;
    const metrics = ctx.measureText(name);
    const pad = 3;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.beginPath();
    ctx.roundRect(sx - metrics.width / 2 - pad, sy - 14 * zoom - pad, metrics.width + pad * 2, 14 * zoom + pad * 2, 3);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.fillText(name, sx, sy);
    ctx.restore();
}

function lighten(hex: string, amount: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, (num >> 16) + amount);
    const g = Math.min(255, ((num >> 8) & 0xff) + amount);
    const b = Math.min(255, (num & 0xff) + amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Checks if a mouse position is near a vertex handle of a selected path.
 */
export function findVertexHandleAt(
    path: MapEntityPath,
    bx: number,
    by: number,
    toScreen: ToScreen,
    threshold: number = 8,
): number {
    const { sx: bsx, sy: bsy } = toScreen(bx, by);
    for (let i = 0; i < path.vertices.length; i++) {
        const handle = toScreen(path.vertices[i].x, path.vertices[i].y);
        const dist = Math.hypot(bsx - handle.sx, bsy - handle.sy);
        if (dist < threshold) {
            return i;
        }
    }
    return -1;
}

/**
 * Checks if a mouse position is on a segment between path vertices.
 */
export function findSegmentIntersection(
    path: MapEntityPath,
    bx: number,
    by: number,
    toScreen: ToScreen,
    threshold: number = 10,
): number {
    const { sx: bsx, sy: bsy } = toScreen(bx, by);
    for (let i = 0; i < path.vertices.length - 1; i++) {
        const a = toScreen(path.vertices[i].x, path.vertices[i].y);
        const b = toScreen(path.vertices[i + 1].x, path.vertices[i + 1].y);
        const dist = pointToSegmentDistance(bsx, bsy, a.sx, a.sy, b.sx, b.sy);
        if (dist < threshold) {
            return i + 1;
        }
    }
    return -1;
}

/**
 * Hit-test for selecting a path.
 */
export function findPathAtPoint(
    paths: MapEntityPath[],
    bx: number,
    by: number,
    toScreen: ToScreen,
    threshold: number,
): string | null {
    let closestUid: string | null = null;
    let closestDist = Infinity;

    for (const path of paths) {
        if (path.vertices.length < 2) continue;
        for (let i = 0; i < path.vertices.length - 1; i++) {
            const a = toScreen(path.vertices[i].x, path.vertices[i].y);
            const b = toScreen(path.vertices[i + 1].x, path.vertices[i + 1].y);
            const { sx: bsx, sy: bsy } = toScreen(bx, by);
            const dist = pointToSegmentDistance(bsx, bsy, a.sx, a.sy, b.sx, b.sy);
            if (dist < closestDist && dist < threshold) {
                closestDist = dist;
                closestUid = path.uid;
            }
        }
    }
    return closestUid;
}

function pointToSegmentDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(px - ax, py - ay);
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
